// src/context/AttendanceContext.jsx

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  getDocs,
  startAfter,
  Timestamp,
  setDoc,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  arrayUnion,

} from "firebase/firestore";
import { db } from "../firebase/config";

// Allowed minutes per break type (admin-configurable defaults)
const BREAK_ALLOWED_MINS = { lunch: 60 };


// ── Context ────────────────────────────────────
const AttendanceContext = createContext(null);

export function useAttendance() {
  const ctx = useContext(AttendanceContext);
  if (!ctx)
    throw new Error("useAttendance must be used inside <AttendanceProvider>");
  return ctx;
}

// ── Provider ───────────────────────────────────
export function AttendanceProvider({ children }) {
  // Today's attendance - realtime for dashboards
  const [todayAttendance, setTodayAttendance] = useState([]);
  const [todayLoading, setTodayLoading] = useState(true);

  // Monthly attendance - paginated for analytics
  const [monthlyAttendance, setMonthlyAttendance] = useState([]);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [monthlyHasMore, setMonthlyHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState(null);

  // Individual user attendance - realtime for current user
  const [userAttendance, setUserAttendance] = useState([]);
  const [userAttendanceLoading, setUserAttendanceLoading] = useState(true);

  // ── Helper functions ─────────────────────────
  const getTodayStr = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const getMonthRange = (year, month) => {
    const start = new Date(year, month - 1, 1);
    const end = new Date(year, month, 0);
    return {
      start: start.toISOString().split("T")[0],
      end: end.toISOString().split("T")[0],
    };
  };

  // ── Optimistic update functions ─────────────────────────
  const optimisticCheckIn = useCallback(
    async (userProfile, mode = "office", shiftStartTime = "09:00") => {
      const userId = userProfile?.uid || userProfile?.id;
      if (!userId)
        throw new Error("Validation Error: Missing user identifier.");

      const today = getTodayStr();
      const now = Timestamp.now();


      // Prevent double check-in
      const existing = todayAttendance.find(
        (r) => r.userId === userId && r.date === today,
      );
      if (existing) {
        throw new Error("You have already checked in today.");
      }

      // Late detection
      const checkInDate = now.toDate();
      const [shiftHour, shiftMin] = shiftStartTime.split(":").map(Number);
      const threshold = new Date(checkInDate);
      threshold.setHours(shiftHour, shiftMin, 0, 0);
      const isLate = checkInDate > threshold;
      const lateByMins = isLate
        ? Math.floor((checkInDate - threshold) / 60000)
        : 0;

      // Create optimistic record
      const docId = `${userId}_${today}`;
      const optimisticRecord = {
        id: docId,
        userId: userId,
        employeeId: userProfile.employeeId || "",
        displayName: userProfile.displayName || "",
        department: userProfile.department || "",
        teamId: userProfile.teamId || null,
        date: today,
        checkIn: now,
        checkOut: null,
        hoursWorked: 0,
        status: "present",
        mode,
        isLate,
        lateByMinutes: lateByMins,
        shiftStartTime,
        breaks: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      // Optimistically add to state
      setTodayAttendance((prev) => [...prev, optimisticRecord]);

      try {
        // Perform Firebase operation
        await setDoc(doc(db, "attendance", docId), {
          userId: userId,
          employeeId: userProfile.employeeId || "",
          displayName: userProfile.displayName || "",
          department: userProfile.department || "",
          teamId: userProfile.teamId || null,
          date: today,
          checkIn: now,
          checkOut: null,
          hoursWorked: 0,
          status: "present",
          mode,
          isLate,
          lateByMinutes: lateByMins,
          shiftStartTime,
          breaks: [],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      } catch (error) {
        // Rollback on error
        setTodayAttendance((prev) =>
          prev.filter((r) => r.id !== optimisticRecord.id),
        );
        throw error;
      }
    },
    [todayAttendance],
  );

  const optimisticCheckOut = useCallback(
    async (attendanceId, checkInTimestamp) => {
      const record = todayAttendance.find((r) => r.id === attendanceId);
      if (!record) throw new Error("Attendance record not found");

      const now = Timestamp.now();
      
      let totalBreakMins = 0;
      const breaks = record.breaks || [];
      const updatedBreaks = [...breaks];
      
      breaks.forEach((b, idx) => {
        if (b.endTime) {
          totalBreakMins += b.duration || 0;
        } else {
          // Active break, calculate duration up to check-out
          const msDuration = now.toDate() - b.startTime.toDate();
          const durationMins = Math.round(msDuration / 60000);
          totalBreakMins += durationMins > 0 ? durationMins : 0;
          updatedBreaks[idx] = {
            ...b,
            endTime: now,
            duration: durationMins > 0 ? durationMins : 0,
          };
        }
      });

      const msGrossWorked = now.toDate() - checkInTimestamp.toDate();
      const grossHours = msGrossWorked / 3_600_000;
      const totalBreakHours = totalBreakMins / 60;
      let hoursWorked = Math.round((grossHours - totalBreakHours) * 100) / 100;
      if (hoursWorked < 0) hoursWorked = 0;

      // Optimistically update the record
      setTodayAttendance((prev) =>
        prev.map((r) => {
          if (r.id === attendanceId) {
            return {
              ...r,
              breaks: updatedBreaks,
              checkOut: now,
              hoursWorked,
              status: "checked-out",
              updatedAt: serverTimestamp(),
            };
          }
          return r;
        }),
      );

      try {
        await updateDoc(doc(db, "attendance", attendanceId), {
          breaks: updatedBreaks,
          checkOut: now,
          hoursWorked,
          status: "checked-out",
          updatedAt: serverTimestamp(),
        });
      } catch (error) {
        // Rollback on error
        setTodayAttendance((prev) =>
          prev.map((r) => {
            if (r.id === attendanceId) {
              return {
                ...r,
                breaks: record.breaks, // Restore original breaks
                checkOut: null,
                hoursWorked: record.hoursWorked,
                status: "present",
              };
            }
            return r;
          }),
        );
        throw error;
      }
    },
    [todayAttendance],
  );

  const optimisticStartBreak = useCallback(async (attendanceId, breakType) => {
    // Validation: prevent starting a break if one is already active
    const existing = todayAttendance.find((r) => r.id === attendanceId);
    if (existing) {
      const alreadyOnBreak = (existing.breaks || []).some((b) => !b.endTime);
      if (alreadyOnBreak) throw new Error("A break is already in progress. End it first.");
    }

    const now = Timestamp.now();
    const allowedMinutes = BREAK_ALLOWED_MINS[breakType] ?? 15;
    const breakRecord = {
      breakType,
      startTime: now,
      endTime: null,
      duration: 0,
      allowedMinutes,
      exceededMinutes: 0,
    };

    // Optimistically update
    setTodayAttendance((prev) =>
      prev.map((record) => {
        if (record.id === attendanceId) {
          return {
            ...record,
            breaks: [...(record.breaks || []), breakRecord],
            status: "on-break",
            updatedAt: serverTimestamp(),
          };
        }
        return record;
      }),
    );

    try {
      await updateDoc(doc(db, "attendance", attendanceId), {
        breaks: arrayUnion(breakRecord),
        status: "on-break",
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      // Rollback
      setTodayAttendance((prev) =>
        prev.map((record) => {
          if (record.id === attendanceId) {
            return {
              ...record,
              breaks: (record.breaks || []).filter((b) => b !== breakRecord),
              status: "present",
            };
          }
          return record;
        }),
      );
      throw error;
    }
  }, [todayAttendance]);

  const optimisticEndBreak = useCallback(
    async (attendanceId) => {
      // Find the record and active break
      const record = todayAttendance.find((r) => r.id === attendanceId);
      if (!record) throw new Error("Attendance record not found");

      const breaks = record.breaks || [];
      const activeBreakIdx = breaks.findIndex((b) => !b.endTime);
      if (activeBreakIdx === -1) throw new Error("No active break to end.");

      const activeBreak = breaks[activeBreakIdx];
      const now = Timestamp.now();
      const msDuration = now.toDate() - activeBreak.startTime.toDate();
      const durationMins = Math.round(msDuration / 60000);
      const allowedMins = activeBreak.allowedMinutes ?? (BREAK_ALLOWED_MINS[activeBreak.breakType] ?? 15);
      const exceededMinutes = Math.max(0, durationMins - allowedMins);

      const updatedBreaks = [...breaks];
      updatedBreaks[activeBreakIdx] = {
        ...activeBreak,
        endTime: now,
        duration: durationMins,
        allowedMinutes: allowedMins,
        exceededMinutes,
      };

      // Optimistically update state
      setTodayAttendance((prev) =>
        prev.map((r) => {
          if (r.id === attendanceId) {
            return {
              ...r,
              breaks: updatedBreaks,
              status: "present",
              updatedAt: serverTimestamp(),
            };
          }
          return r;
        }),
      );

      try {
        await updateDoc(doc(db, "attendance", attendanceId), {
          breaks: updatedBreaks,
          status: "present",
          updatedAt: serverTimestamp(),
        });

        // Auto-create exceeded break report if over limit
        if (exceededMinutes > 0) {
          await addDoc(collection(db, "exceededBreakReports"), {
            attendanceId,
            employeeId: record.employeeId || "",
            employeeName: record.displayName || "",
            department: record.department || "",
            shiftId: record.shiftId || "",
            breakType: activeBreak.breakType,
            breakStart: activeBreak.startTime,
            breakEnd: now,
            allowedMinutes: allowedMins,
            actualMinutes: durationMins,
            exceededMinutes,
            date: record.date || new Date().toISOString().split("T")[0],
            createdAt: serverTimestamp(),
          });
        }
      } catch (error) {
        // Rollback on error
        setTodayAttendance((prev) =>
          prev.map((r) => {
            if (r.id === attendanceId) {
              return { ...r, breaks, status: "on-break" };
            }
            return r;
          }),
        );
        throw error;
      }
    },
    [todayAttendance],
  );

  // ── Today's attendance listener (single realtime subscription) ──
  useEffect(() => {
    const today = getTodayStr();
    const q = query(collection(db, "attendance"), where("date", "==", today));

    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setTodayAttendance(data);
      setTodayLoading(false);
    });

    return unsub;
  }, []);

  // ── Load monthly attendance (paginated, one-time fetch) ──
  const loadMonthlyAttendance = useCallback(
    async (year, month, reset = false) => {
      if (reset) {
        setMonthlyAttendance([]);
        setLastDoc(null);
        setMonthlyHasMore(true);
      }

      setMonthlyLoading(true);
      const { start, end } = getMonthRange(year, month);

      let q = query(
        collection(db, "attendance"),
        where("date", ">=", start),
        where("date", "<=", end),
        orderBy("date", "desc"),
        limit(50), // Load 50 at a time
      );

      if (!reset && lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      try {
        const snap = await getDocs(q);
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        if (reset) {
          setMonthlyAttendance(data);
        } else {
          setMonthlyAttendance((prev) => [...prev, ...data]);
        }

        setLastDoc(snap.docs[snap.docs.length - 1] || null);
        setMonthlyHasMore(snap.docs.length === 50);
      } catch (error) {
        console.error("Error loading monthly attendance:", error);
      } finally {
        setMonthlyLoading(false);
      }
    },
    [lastDoc],
  );

  // ── User attendance listener ──
  const subscribeToUserAttendance = useCallback((userId) => {
    if (!userId) {
      setUserAttendance([]);
      setUserAttendanceLoading(false);
      return () => {};
    }

    setUserAttendanceLoading(true);
    const q = query(
      collection(db, "attendance"),
      where("userId", "==", userId),
      orderBy("date", "desc"),
      limit(30), // Last 30 days
    );

    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setUserAttendance(data);
      setUserAttendanceLoading(false);
    });

    return unsub;
  }, []);

  // ── Context value ────────────────────────────
  const value = React.useMemo(
    () => ({
      // Today's attendance (realtime)
      todayAttendance,
      todayLoading,

      // Monthly attendance (paginated)
      monthlyAttendance,
      monthlyLoading,
      monthlyHasMore,
      loadMonthlyAttendance,

      // User attendance (realtime when subscribed)
      userAttendance,
      userAttendanceLoading,
      subscribeToUserAttendance,

      // Optimistic write operations
      checkIn: optimisticCheckIn,
      checkOut: optimisticCheckOut,
      startBreak: optimisticStartBreak,
      endBreak: optimisticEndBreak,
    }),
    [
      todayAttendance,
      todayLoading,
      monthlyAttendance,
      monthlyLoading,
      monthlyHasMore,
      loadMonthlyAttendance,
      userAttendance,
      userAttendanceLoading,
      subscribeToUserAttendance,
      optimisticCheckIn,
      optimisticCheckOut,
      optimisticStartBreak,
      optimisticEndBreak,
    ],
  );

  return (
    <AttendanceContext.Provider value={value}>
      {children}
    </AttendanceContext.Provider>
  );
}
